export function productChangeKey(sectionIdx, productIdx) {
  return `${sectionIdx}-${productIdx}`;
}

export function convertTimeToInputFormat(timeStr) {
  if (!timeStr || !String(timeStr).trim()) return '';

  const trimmed = String(timeStr).trim();

  if (/^\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  try {
    const time = trimmed.toUpperCase();
    const match = time.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)/);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2] ? parseInt(match[2], 10) : 0;
      const period = match[3];

      if (hours < 1 || hours > 12) return '';

      if (period === 'PM' && hours !== 12) hours += 12;
      else if (period === 'AM' && hours === 12) hours = 0;

      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }
    }

    const timeOnlyMatch = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (timeOnlyMatch) {
      const hours = parseInt(timeOnlyMatch[1], 10);
      const minutes = parseInt(timeOnlyMatch[2], 10);
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }
    }
  } catch (e) {
    return '';
  }

  return '';
}

export function convertTimeToReadableFormat(timeStr) {
  if (!timeStr || !String(timeStr).trim()) return '';
  if (/AM|PM/i.test(timeStr)) return timeStr;
  try {
    const [hours, minutes] = String(timeStr).split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return timeStr;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
    const displayMinutes = String(minutes).padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${period}`;
  } catch (e) {
    return timeStr;
  }
}

const DATE_TIME_FIELD_KEYS = [
  'startDate',
  'endDate',
  'deliveryTime',
  'strikeTime',
  'hardSetTime',
  'hardOutTime',
  'hardOutDate'
];
const TIME_FIELD_KEYS = new Set(['deliveryTime', 'strikeTime', 'hardSetTime', 'hardOutTime']);

function normalizeDateValue(value) {
  const match = String(value || '').trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : String(value || '').trim();
}

function normalizeDateTimeValue(key, value) {
  if (TIME_FIELD_KEYS.has(key)) return convertTimeToInputFormat(value || '');
  if (key === 'startDate' || key === 'endDate' || key === 'hardOutDate') return normalizeDateValue(value);
  return String(value || '').trim();
}

export function getOriginalDateTime(proposal = {}) {
  return {
    startDate: normalizeDateValue(proposal.startDate || ''),
    endDate: normalizeDateValue(proposal.endDate || ''),
    deliveryTime: convertTimeToInputFormat(proposal.deliveryTime || ''),
    strikeTime: convertTimeToInputFormat(proposal.strikeTime || ''),
    hardSetTime: convertTimeToInputFormat(proposal.hardSetTime || ''),
    hardOutTime: convertTimeToInputFormat(proposal.hardOutTime || ''),
    hardOutDate: normalizeDateValue(proposal.hardOutDate || '')
  };
}

export function createEmptyDraft(proposal = {}) {
  return {
    quantityChanges: {},
    dateTimeChanges: getOriginalDateTime(proposal),
    newProducts: [],
    miscNotes: ''
  };
}

export function applyQuantityChange(draft, {
  sectionIdx,
  productIdx,
  originalQuantity,
  newQuantity,
  productName,
  sectionName,
  removed = false
}) {
  const key = productChangeKey(sectionIdx, productIdx);
  const quantityChanges = { ...draft.quantityChanges };
  const parsedOriginal = parseInt(originalQuantity, 10) || 0;
  const parsedNew = parseInt(newQuantity, 10) || 0;
  const isRemoved = removed || parsedNew === 0;

  if (!isRemoved && parsedNew === parsedOriginal) {
    delete quantityChanges[key];
  } else {
    quantityChanges[key] = {
      sectionIdx,
      productIdx,
      originalQuantity: parsedOriginal,
      newQuantity: isRemoved ? 0 : parsedNew,
      productName: productName || '',
      sectionName: sectionName || '',
      removed: isRemoved
    };
  }

  return { ...draft, quantityChanges };
}

export function clearProductChange(draft, sectionIdx, productIdx) {
  const key = productChangeKey(sectionIdx, productIdx);
  if (!draft.quantityChanges[key]) return draft;
  const quantityChanges = { ...draft.quantityChanges };
  delete quantityChanges[key];
  return { ...draft, quantityChanges };
}

export function getProductChange(draft, sectionIdx, productIdx) {
  return draft?.quantityChanges?.[productChangeKey(sectionIdx, productIdx)] || null;
}

export function getDisplayedQuantity(product, change) {
  if (!change) return parseInt(product?.quantity, 10) || 0;
  return parseInt(change.newQuantity, 10) || 0;
}

export function isProductRemoved(change) {
  return !!(change && (change.removed || change.newQuantity === 0));
}

export function countProductChanges(draft) {
  if (!draft) return 0;
  return Object.keys(draft.quantityChanges || {}).length + (draft.newProducts || []).length;
}

export function hasProductEdits(draft) {
  return countProductChanges(draft) > 0;
}

export function hasDateTimeChanges(draft, proposal = {}) {
  return Object.keys(getChangedDateTimeFields(draft, proposal)).length > 0;
}

export function getChangedDateTimeFields(draft, proposal = {}) {
  const original = getOriginalDateTime(proposal);
  const dt = draft?.dateTimeChanges || {};
  const changed = {};
  DATE_TIME_FIELD_KEYS.forEach((key) => {
    const next = normalizeDateTimeValue(key, dt[key]);
    const prev = normalizeDateTimeValue(key, original[key]);
    if (next === prev) return;
    changed[key] = TIME_FIELD_KEYS.has(key) ? convertTimeToReadableFormat(dt[key]) : next;
  });
  return changed;
}

export function hasAnyChanges(draft, proposal = {}) {
  return (
    hasProductEdits(draft) ||
    hasDateTimeChanges(draft, proposal) ||
    !!(draft?.miscNotes && String(draft.miscNotes).trim())
  );
}

function parseOptionalPrice(value) {
  if (value === null || value === undefined || value === '') return '';
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : '';
}

export function buildNewProductRequest({
  name,
  quantity,
  section,
  existingSectionNames = [],
  sourceSection = '',
  imageUrl = '',
  price = '',
  dimensions = '',
  notes = ''
} = {}) {
  const sectionName = String(section || '').trim();
  const existing = new Set(
    (existingSectionNames || []).map((item) => String(item || '').trim()).filter(Boolean)
  );
  return {
    name: String(name || '').trim(),
    quantity: Math.max(1, parseInt(quantity, 10) || 1),
    section: sectionName,
    isNewSection: !!sectionName && !existing.has(sectionName),
    sourceSection: String(sourceSection || '').trim(),
    imageUrl: imageUrl || '',
    price: parseOptionalPrice(price),
    dimensions: String(dimensions || '').trim(),
    notes: notes || ''
  };
}

export function canSubmitNewProductRequest(product) {
  return !!(
    product &&
    String(product.name || '').trim() &&
    (parseInt(product.quantity, 10) || 0) >= 1 &&
    String(product.section || '').trim()
  );
}

export function formatNewProductPlacement(product = {}) {
  const quantity = Math.max(1, parseInt(product.quantity, 10) || 1);
  const section = String(product.sectionName || product.section || '').trim();
  if (product.isNewSection) {
    return `Qty. ${quantity} · New section: ${section}`;
  }
  return section ? `Qty. ${quantity} · ${section}` : `Qty. ${quantity}`;
}

export function getNewProductRequestsForSection(draft, sectionName) {
  const name = sectionName || '';
  return (draft?.newProducts || [])
    .map((product, index) => ({ ...product, index }))
    .filter((product) => (product.section || '') === name || (product.sourceSection || '') === name);
}

export function getNewProductRequestsOutsideSections(draft, sectionNames = []) {
  const known = new Set((sectionNames || []).map((name) => String(name || '').trim()).filter(Boolean));
  return (draft?.newProducts || [])
    .map((product, index) => ({ ...product, index }))
    .filter((product) => !known.has(String(product.section || '').trim()));
}

export function summarizeProductChanges(draft, sections = []) {
  const quantityItems = Object.values(draft?.quantityChanges || {}).map((change) => {
    const section = sections[change.sectionIdx];
    const product = section?.products?.[change.productIdx];
    return {
      key: productChangeKey(change.sectionIdx, change.productIdx),
      kind: change.removed || change.newQuantity === 0 ? 'removed' : 'quantity',
      sectionName: change.sectionName || section?.name || 'Untitled section',
      productName: change.productName || product?.name || '',
      originalQuantity: change.originalQuantity,
      newQuantity: change.newQuantity,
      removed: !!(change.removed || change.newQuantity === 0),
      imageUrl: product?.imageUrl || product?.image || change.imageUrl || ''
    };
  });

  const newItems = (draft?.newProducts || []).map((product, index) => ({
    key: `new-${index}`,
    kind: 'new',
    sectionName: product.section || 'Untitled section',
    productName: product.name || '',
    quantity: Math.max(1, parseInt(product.quantity, 10) || 1),
    isNewSection: !!product.isNewSection,
    notes: product.notes || '',
    imageUrl: product.imageUrl || product.image || '',
    index
  }));

  const grouped = [];
  const bySection = new Map();

  [...quantityItems, ...newItems].forEach((item) => {
    const sectionName = item.sectionName || 'Untitled section';
    if (!bySection.has(sectionName)) {
      const group = { sectionName, items: [] };
      bySection.set(sectionName, group);
      grouped.push(group);
    }
    bySection.get(sectionName).items.push(item);
  });

  return { quantityItems, newItems, grouped };
}

export function buildRemovedProducts(draft) {
  return Object.values(draft?.quantityChanges || {})
    .filter((change) => change.removed || change.newQuantity === 0)
    .map((change) => ({
      name: change.productName,
      section: change.sectionName,
      originalQuantity: change.originalQuantity
    }));
}

export function buildChangeRequestPayload({ draft, proposal, contactName = '' }) {
  const continueEditing = isPendingReviewProposal(proposal);
  return {
    type: 'changeRequest',
    contactName,
    projectNumber: proposal.projectNumber,
    version: proposal.version,
    changeRequestId: String(proposal?.changeRequestId || '').trim(),
    continueEditing,
    source: 'client_portal',
    timestamp: new Date().toISOString(),
    changes: {
      quantityChanges: draft.quantityChanges,
      dateTimeChanges: getChangedDateTimeFields(draft, proposal),
      newProducts: draft.newProducts,
      removedProducts: buildRemovedProducts(draft),
      miscNotes: String(draft.miscNotes || '').trim()
    },
    originalProposal: {
      projectNumber: proposal.projectNumber,
      version: proposal.version,
      clientName: proposal.clientName,
      venueName: proposal.venueName,
      startDate: proposal.startDate || '',
      endDate: proposal.endDate || '',
      deliveryTime: proposal.deliveryTime || '',
      strikeTime: proposal.strikeTime || '',
      hardSetTime: proposal.hardSetTime || '',
      hardOutTime: proposal.hardOutTime || '',
      hardOutDate: proposal.hardOutDate || '',
      rwTicketNumber: proposal.rwTicketNumber || proposal.rwTicket || ''
    }
  };
}

export function getContactNameFromSession(session) {
  return (
    session?.clientInfo?.fullName ||
    [session?.clientInfo?.firstName, session?.clientInfo?.lastName].filter(Boolean).join(' ').trim() ||
    ''
  );
}

function isPublishedProposal(proposal) {
  if (!proposal) return false;
  if (proposal.published === undefined || proposal.published === null || proposal.published === '') {
    return true;
  }
  return proposal.published === true || proposal.published === 'true';
}

export function getProposalVersionNumber(proposal) {
  const parsed = parseInt(proposal?.version, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

export function proposalMatchesProjectVersion(proposal, projectNumber, version) {
  if (!proposal) return false;
  return (
    String(proposal.projectNumber || '').trim() === String(projectNumber || '').trim() &&
    getProposalVersionNumber(proposal) === getProposalVersionNumber({ version })
  );
}

export function applyDraftToProposal(proposal, draft, { version, changeRequestId } = {}) {
  let sections = [];
  try {
    sections = JSON.parse(proposal?.sectionsJSON || '[]');
  } catch (_) {
    sections = [];
  }

  const nextSections = (sections || []).map((section, sectionIdx) => {
    const products = (section.products || []).map((product, productIdx) => {
      const change = draft?.quantityChanges?.[productChangeKey(sectionIdx, productIdx)];
      if (!change) return product;
      return {
        ...product,
        quantity: change.newQuantity
      };
    });
    return { ...section, products };
  });

  (draft?.newProducts || []).forEach((newProduct) => {
    const sectionName = String(newProduct.section || '').trim() || 'Untitled section';
    let target = nextSections.find((section) => section.name === sectionName);
    if (!target) {
      target = { name: sectionName, products: [] };
      nextSections.push(target);
    }
    const parsedPrice = parseFloat(newProduct.price);
    target.products.push({
      name: String(newProduct.name || '').trim(),
      quantity: Math.max(1, parseInt(newProduct.quantity, 10) || 1),
      price: Number.isFinite(parsedPrice) ? parsedPrice : 0,
      imageUrl: newProduct.imageUrl || newProduct.image || '',
      dimensions: newProduct.dimensions || '',
      note: newProduct.notes || ''
    });
  });

  const dateTime = draft?.dateTimeChanges || {};
  return {
    ...proposal,
    version: getProposalVersionNumber({ version: version || getProposalVersionNumber(proposal) + 1 }),
    published: false,
    pendingReview: true,
    isFromChangeRequest: true,
    changeRequestId: String(changeRequestId || proposal?.changeRequestId || '').trim(),
    startDate: dateTime.startDate || proposal?.startDate || '',
    endDate: dateTime.endDate || proposal?.endDate || '',
    deliveryTime: convertTimeToReadableFormat(dateTime.deliveryTime) || proposal?.deliveryTime || '',
    strikeTime: convertTimeToReadableFormat(dateTime.strikeTime) || proposal?.strikeTime || '',
    hardSetTime: convertTimeToReadableFormat(dateTime.hardSetTime) || proposal?.hardSetTime || '',
    hardOutTime: convertTimeToReadableFormat(dateTime.hardOutTime) || proposal?.hardOutTime || '',
    hardOutDate: dateTime.hardOutDate || proposal?.hardOutDate || '',
    venueName: dateTime.venueName || proposal?.venueName || '',
    sectionsJSON: JSON.stringify(nextSections)
  };
}

export function resolveSubmittedChangeRequest(result, { projectNumber = '', currentVersion } = {}) {
  const resolvedProject = String(result?.projectNumber || projectNumber || '').trim();
  const returnedVersion = parseInt(result?.version, 10);
  const current = parseInt(currentVersion, 10);
  const inferredVersion = Number.isFinite(current) && current > 0 ? current + 1 : null;
  const version = Number.isFinite(returnedVersion) && returnedVersion > 0
    ? returnedVersion
    : inferredVersion;
  const explicitlyFailed = result?.proposalCreated === false;
  return {
    created: !explicitlyFailed && Number.isFinite(version) && version > 0,
    projectNumber: resolvedProject,
    version: Number.isFinite(version) && version > 0 ? version : null,
    pendingReview: true,
    changeRequestId: String(result?.changeRequestId || '').trim(),
    inferred: !(Number.isFinite(returnedVersion) && returnedVersion > 0),
    message: String(result?.message || '')
  };
}

export async function waitForClientProposalVersion({
  fetchProposal,
  projectNumber,
  version,
  timeoutMs = 18000,
  intervalMs = 1200
}) {
  const deadline = Date.now() + timeoutMs;
  let lastError = null;
  let attempts = 0;
  while (Date.now() <= deadline) {
    attempts += 1;
    try {
      const proposal = await fetchProposal(projectNumber, version);
      if (proposalMatchesProjectVersion(proposal, projectNumber, version)) {
        return proposal;
      }
    } catch (err) {
      lastError = err;
    }
    if (Date.now() + intervalMs > deadline) break;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  const error = new Error('PENDING_REVIEW_NOT_READY');
  error.code = 'PENDING_REVIEW_NOT_READY';
  error.cause = lastError;
  error.attempts = attempts;
  throw error;
}

/** Client-visible unpublished change-request version. Never treat as Published. */
export function isPendingReviewProposal(proposal) {
  if (!proposal) return false;
  if (isPublishedProposal(proposal)) return false;
  if (proposal.isFromNewSubmission === true) return false;
  if (proposal.pendingReview === true) return true;
  return (
    proposal.isFromChangeRequest === true ||
    String(proposal.changeRequestId || '').trim() !== ''
  );
}

/** Latest pending-review version wins until a newer published revision exists. */
export function selectActiveClientProposal(proposals, projectNumber) {
  const rows = (proposals || []).filter(
    (proposal) => String(proposal?.projectNumber || '').trim() === String(projectNumber || '').trim()
  );
  if (!rows.length) return null;

  let latestPublished = null;
  let latestPending = null;
  rows.forEach((proposal) => {
    const version = getProposalVersionNumber(proposal);
    if (isPendingReviewProposal(proposal)) {
      if (!latestPending || version > getProposalVersionNumber(latestPending)) {
        latestPending = proposal;
      }
      return;
    }
    if (isPublishedProposal(proposal)) {
      if (!latestPublished || version > getProposalVersionNumber(latestPublished)) {
        latestPublished = proposal;
      }
    }
  });

  if (
    latestPending &&
    (!latestPublished || getProposalVersionNumber(latestPending) > getProposalVersionNumber(latestPublished))
  ) {
    return latestPending;
  }
  return latestPublished || latestPending || rows[0];
}
